import { useEffect, useId, useState } from "react";
import { Link } from "react-router-dom";
import { Trans } from "react-i18next";
import { useHydrated } from "@/hooks/useHydrated";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import {
  acceptAllCookies,
  getCookieConsent,
  rejectNonEssentialCookies,
  setAnalyticsConsent,
  subscribeCookieConsent,
} from "@/utils/cookieConsent";

/* Accept and reject share one style: the AEPD guide requires equal prominence. */
const choiceBtn =
  "min-h-11 flex-1 rounded-full bg-brand-primary px-5 text-sm font-semibold text-white transition-colors hover:bg-navy-deep";

export function CookieBanner() {
  const hydrated = useHydrated();
  if (!hydrated) return null;
  return <CookieBannerInner />;
}

function CookieBannerInner() {
  const { t } = useAppTranslation("cookies");
  const titleId = useId();
  const descId = useId();
  const switchId = useId();
  const [visible, setVisible] = useState(() => getCookieConsent() === null);
  const [showSettings, setShowSettings] = useState(false);
  const [analytics, setAnalytics] = useState(() => getCookieConsent()?.analytics ?? false);

  useEffect(
    () =>
      subscribeCookieConsent((consent) => {
        setVisible(consent === null);
        setShowSettings(false);
        if (consent) setAnalytics(consent.analytics);
      }),
    [],
  );

  if (!visible) return null;

  return (
    <section
      role="dialog"
      aria-modal="false"
      aria-labelledby={titleId}
      aria-describedby={descId}
      className="fixed inset-x-3 bottom-3 z-[200] rounded-3xl border border-brand-primary/10 bg-white p-5 shadow-lit-lg sm:bottom-6 sm:left-6 sm:right-auto sm:w-[27rem] sm:p-6"
    >
      <div>
        <div>
          <h2 id={titleId} className="font-display text-lg font-semibold text-brand-primary">
            {t("title")}
          </h2>
          <p id={descId} className="mt-2 text-sm leading-relaxed text-ink-soft">
            <Trans
              i18nKey="description"
              ns="cookies"
              components={{
                1: <Link to="/cookies" className="font-medium text-teal-strong underline underline-offset-2" />,
                3: <Link to="/privacidad" className="font-medium text-teal-strong underline underline-offset-2" />,
              }}
            />
          </p>

          {showSettings ? (
            <div className="mt-4 rounded-2xl border border-brand-primary/10 bg-brand-bg p-4">
              <div className="flex items-start justify-between gap-4">
                <label htmlFor={switchId} className="cursor-pointer">
                  <span className="block text-sm font-semibold text-brand-primary">{t("analyticsTitle")}</span>
                  <span className="mt-0.5 block text-xs leading-relaxed text-ink-muted">{t("analyticsDesc")}</span>
                </label>
                <button
                  id={switchId}
                  type="button"
                  role="switch"
                  aria-checked={analytics}
                  onClick={() => setAnalytics((v) => !v)}
                  className={`relative mt-0.5 h-7 w-12 shrink-0 rounded-full transition-colors ${analytics ? "bg-teal-strong" : "bg-ink-muted/40"}`}
                >
                  <span
                    aria-hidden="true"
                    className={`absolute left-1 top-1 size-5 rounded-full bg-white shadow transition-transform ${analytics ? "translate-x-5" : ""}`}
                  />
                </button>
              </div>
              <p className="mt-3 text-xs text-ink-muted">{t("necessaryNote")}</p>
            </div>
          ) : null}

          <div className="mt-5 flex gap-2.5">
            <button type="button" onClick={acceptAllCookies} className={choiceBtn}>
              {t("acceptAll")}
            </button>
            <button type="button" onClick={rejectNonEssentialCookies} className={choiceBtn}>
              {t("rejectNonEssential")}
            </button>
          </div>
          <button
            type="button"
            aria-expanded={showSettings}
            onClick={() => (showSettings ? setAnalyticsConsent(analytics) : setShowSettings(true))}
            className="mt-3 min-h-10 w-full rounded-full text-sm font-semibold text-brand-primary underline decoration-brand-secondary decoration-2 underline-offset-4 transition-colors hover:bg-teal-soft"
          >
            {showSettings ? t("savePreferences") : t("configure")}
          </button>
        </div>
      </div>
    </section>
  );
}
